import React, { useState, useEffect, useRef, useContext, useMemo } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, differenceInMinutes } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@mui/styles";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { sanitizePiiPreview } from "../../helpers/sanitizePii";
import { Tooltip } from "@mui/material";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import TransferTicketModal from "../TransferTicketModal";
import { check, Can } from "../../components/Can";
import { maybeCreateDriveFolder } from "../DriveFolderPrompt";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		cursor: "pointer",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		paddingRight: 20,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: "#4caf50",
	},

	unreadHighlight: {
		borderLeft: "3px solid #4caf50",
		backgroundColor: "rgba(76,175,80,0.15)",
	},

	acceptButton: {
		flex: "0 0 auto",
		marginLeft: 8,
		alignSelf: "center",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

actionMenu: {
		flex: "0 0 auto",
		alignSelf: "center",
		marginLeft: 4,
	},

	slaBadge: {
		display: "inline-block",
		borderRadius: 10,
		padding: "1px 6px",
		fontSize: "0.72em",
		fontWeight: 600,
		whiteSpace: "nowrap",
		marginLeft: 4,
	},

	priorityBadge: {
		display: "inline-block",
		borderRadius: 10,
		padding: "1px 6px",
		fontSize: "0.72em",
		fontWeight: 700,
		whiteSpace: "nowrap",
		marginLeft: 4,
		letterSpacing: "0.02em",
	},
}));

const TicketListItem = ({ ticket, isSelected, onSelect }) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [menuAnchor, setMenuAnchor] = useState(null);
	const [transferModalOpen, setTransferModalOpen] = useState(false);

	const PRIORITY_CONFIG = {
		low:    { label: "↓ Baixa",   color: "#64b5f6", bg: "rgba(100,181,246,0.15)" },
		normal: null,
		high:   { label: "↑ Alta",    color: "#ff8f00", bg: "rgba(255,143,0,0.15)" },
		urgent: { label: "⚠ Urgente", color: "#d32f2f", bg: "rgba(211,47,47,0.15)" },
	};

	const priorityBadge = ticket.priority && ticket.priority !== "normal"
		? PRIORITY_CONFIG[ticket.priority]
		: null;

	const slaBadge = useMemo(() => {
		if (ticket.status === "closed" || !ticket.updatedAt) return null;
		if (ticket.status === "open" && !ticket.unreadMessages) return null;
		const slaWarn = parseInt(localStorage.getItem("sla_warning_minutes") || "30", 10);
		const slaCrit = parseInt(localStorage.getItem("sla_critical_minutes") || "120", 10);
		const minutes = differenceInMinutes(new Date(), parseISO(ticket.updatedAt));
		if (minutes < slaWarn) return null;
		const hours = Math.floor(minutes / 60);
		const label = hours >= 1 ? `${hours}h` : `${minutes}m`;
		const color = minutes >= slaCrit ? "#d32f2f" : "#e65100";
		return { label, color };
	}, [ticket.status, ticket.updatedAt, ticket.unreadMessages]);

	const canTransfer = check(user?.profile, "ticket-options:transferWhatsapp");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			if (isMounted.current) setLoading(false);
			toastError(err);
			return;
		}
		if (isMounted.current) {
			setLoading(false);
		}
		maybeCreateDriveFolder(ticket.contact);
		navigate(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		navigate(`/tickets/${id}`);
	};

	const handleResolveTicket = async e => {
		e.stopPropagation();
		setMenuAnchor(null);
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, { status: "closed", userId: user?.id });
			toast.success("Ticket resolvido");
		} catch (err) {
			toastError(err);
		}
		if (isMounted.current) setLoading(false);
	};

	return (
		<React.Fragment key={ticket.id}>
			<TransferTicketModal
				modalOpen={transferModalOpen}
				onClose={() => setTransferModalOpen(false)}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
				ticketUserId={ticket.userId}
			/>
			<ListItem
				dense
				button
				onClick={e => {
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
					[classes.unreadHighlight]: ticket.unreadMessages > 0 && (!ticketId || +ticketId !== ticket.id),
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				{onSelect && (
					<Checkbox
						size="small"
						checked={!!isSelected}
						onClick={e => { e.stopPropagation(); onSelect(ticket.id); }}
						sx={{ p: "4px", mr: "-4px" }}
					/>
				)}
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl} sx={{ width: 48, height: 48 }} />
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
							>
								{ticket?.contact?.name || ticket?.contact?.number || "—"}
							</Typography>
							<Typography
								className={classes.lastMessageTime}
								component="span"
								variant="body2"
								color="textSecondary"
							>
								{ticket.updatedAt
									? isSameDay(parseISO(ticket.updatedAt), new Date())
										? format(parseISO(ticket.updatedAt), "HH:mm")
										: format(parseISO(ticket.updatedAt), "dd/MM/yyyy")
									: ""}
							</Typography>
						</span>
					}
					secondary={
						<span className={classes.contactNameWrapper}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
							>
								{ticket.lastMessage ? (
									<MarkdownWrapper>{sanitizePiiPreview(ticket.lastMessage)}</MarkdownWrapper>
								) : (
									<br />
								)}
							</Typography>
							<span style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
								{ticket.status === "closed" && (
									<Badge
										className={classes.closedBadge}
										badgeContent={"closed"}
										color="primary"
									/>
								)}
{priorityBadge && (
									<Tooltip title="Prioridade">
										<span
											className={classes.priorityBadge}
											style={{ background: priorityBadge.bg, color: priorityBadge.color, border: `1px solid ${priorityBadge.color}` }}
										>
											{priorityBadge.label}
										</span>
									</Tooltip>
								)}
								{slaBadge && (
									<Tooltip title="Tempo sem resposta">
										<span
											className={classes.slaBadge}
											style={{ background: slaBadge.color, color: "#fff" }}
										>
											⏱ {slaBadge.label}
										</span>
									</Tooltip>
								)}
								<Badge
									className={classes.newMessagesCount}
									badgeContent={ticket.unreadMessages}
									classes={{ badge: classes.badgeStyle }}
								/>
							</span>
						</span>
					}
				/>
				{ticket.status === "pending" && (
					<ButtonWithSpinner
						color="primary"
						variant="contained"
						className={classes.acceptButton}
						size="small"
						loading={loading}
						onClick={e => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
					>
						{i18n.t("ticketsList.buttons.accept")}
					</ButtonWithSpinner>
				)}
				{ticket.status === "open" && (
					<>
						<IconButton
							size="small"
							className={classes.actionMenu}
							onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
						>
							<MoreVertIcon fontSize="small" />
						</IconButton>
						<Menu
							anchorEl={menuAnchor}
							open={Boolean(menuAnchor)}
							onClose={() => setMenuAnchor(null)}
							onClick={e => e.stopPropagation()}
						>
							<MenuItem onClick={handleResolveTicket}>Resolver</MenuItem>
							<MenuItem onClick={e => {
								e.stopPropagation();
								setMenuAnchor(null);
								api.post(`/tickets/${ticket.id}/mark-unread`).catch(toastError);
							}}>
								Marcar como não lido
							</MenuItem>
							{[
								{ value: "urgent", label: "⚠ Urgente" },
								{ value: "high",   label: "↑ Alta" },
								{ value: "normal", label: "— Normal" },
								{ value: "low",    label: "↓ Baixa" },
							].map(p => (
								<MenuItem
									key={p.value}
									selected={ticket.priority === p.value}
									onClick={e => {
										e.stopPropagation();
										setMenuAnchor(null);
										api.put(`/tickets/${ticket.id}`, { priority: p.value }).catch(toastError);
									}}
								>
									Prioridade: {p.label}
								</MenuItem>
							))}
							{canTransfer && (
								<MenuItem onClick={e => { e.stopPropagation(); setMenuAnchor(null); setTransferModalOpen(true); }}>
									Transferir
								</MenuItem>
							)}
							<Can
								role={user?.profile}
								perform="ticket-options:deleteTicket"
								yes={() => (
									<MenuItem
										onClick={e => {
											e.stopPropagation();
											setMenuAnchor(null);
											if (window.confirm("Deletar este ticket?")) {
												api.delete(`/tickets/${ticket.id}`).catch(toastError);
											}
										}}
										style={{ color: "#d32f2f" }}
									>
										Deletar
									</MenuItem>
								)}
							/>
						</Menu>
					</>
				)}
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
